import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  listLiveMeetings,
  listLiveViews,
  formatDuration,
  getMeetingUrl,
  type ZCallMeeting,
  type ZCallLiveView,
} from '@/lib/zcall';

interface Props {
  userId: number;
}

const DURATION_OPTIONS = [
  { value: 60, label: '1h' },
  { value: 90, label: '1h30' },
  { value: 120, label: '2h' },
  { value: 180, label: '3h' },
];

function genRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const seg = (len: number): string => {
    const bytes = new Uint8Array(len);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
    else for (let i = 0; i < len; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, (b) => chars[b % chars.length]).join('');
  };
  return `zc-${seg(6)}-${seg(6)}-${seg(6)}-${seg(4)}`;
}

function genSlug(title: string): string {
  const base = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `${base || 'live'}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'À venir',
  active: 'En direct',
  ended: 'Terminé',
  cancelled: 'Annulé',
};

const STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-red-50 text-red-600 border-red-200',
  ended: 'bg-background-100 text-foreground-400 border-background-200',
  cancelled: 'bg-background-100 text-foreground-400 border-background-200',
};

/** Tableau de bord des diffusions ZCall : lives passés/à venir + spectateurs. */
export default function LiveDashboard({ userId }: Props) {
  const [meetings, setMeetings] = useState<ZCallMeeting[]>([]);
  const [views, setViews] = useState<ZCallLiveView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('20:00');
  const [duration, setDuration] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listLiveMeetings(userId);
      setMeetings(list);
      setViews(await listLiveViews(list.map((m) => m.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Agrégat de spectateurs par live. */
  const statsByMeeting = useMemo(() => {
    const map = new Map<number, { unique: number; watch: number }>();
    const seenByMeeting = new Map<number, Set<string>>();
    views.forEach((v) => {
      const mid = Number(v.meeting_id);
      if (!mid) return;
      if (!seenByMeeting.has(mid)) seenByMeeting.set(mid, new Set());
      seenByMeeting.get(mid)!.add(v.viewer_key || String(v.id));
      const cur = map.get(mid) || { unique: 0, watch: 0 };
      cur.watch += Number(v.watch_seconds) || 0;
      map.set(mid, cur);
    });
    seenByMeeting.forEach((set, mid) => {
      const cur = map.get(mid) || { unique: 0, watch: 0 };
      cur.unique = set.size;
      map.set(mid, cur);
    });
    return map;
  }, [views]);

  const now = Date.now();
  const upcoming = meetings.filter((m) => m.status === 'scheduled' && (!m.scheduled_at || new Date(m.scheduled_at).getTime() > now));
  const past = meetings.filter((m) => !(m.status === 'scheduled' && (!m.scheduled_at || new Date(m.scheduled_at).getTime() > now)));

  const totalUnique = Array.from(statsByMeeting.values()).reduce((s, v) => s + v.unique, 0);
  const totalWatch = Array.from(statsByMeeting.values()).reduce((s, v) => s + v.watch, 0);
  const peakOverall = meetings.reduce((max, m) => Math.max(max, Number((m as unknown as { live_peak_viewers?: number }).live_peak_viewers) || 0), 0);

  const createLive = async (instant: boolean) => {
    if (!title.trim()) { setError('Donnez un titre à votre live.'); return; }
    setBusy(true);
    setError(null);
    const roomId = genRoomId();
    const slug = genSlug(title);
    const { error: insErr } = await supabase.from('zcall_meetings').insert({
      user_id: userId,
      title: title.trim(),
      room_id: roomId,
      slug,
      scheduled_at: instant ? null : `${date}T${time}:00`,
      duration_minutes: Math.max(60, duration),
      status: instant ? 'active' : 'scheduled',
      kind: 'live',
    });
    setBusy(false);
    if (insErr) { setError(insErr.message); return; }
    setTitle('');
    setDate('');
    void load();
  };

  const endLive = async (id: number) => {
    await supabase.from('zcall_meetings').update({ status: 'ended' }).eq('id', id);
    void load();
  };

  const copyLink = (m: ZCallMeeting) => {
    void navigator.clipboard.writeText(getMeetingUrl('', m)).then(() => {
      setCopiedId(m.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const cards = [
    { icon: 'ri-live-line', label: 'Lives diffusés', value: past.length, tone: 'text-primary-600 bg-primary-50' },
    { icon: 'ri-calendar-event-line', label: 'À venir', value: upcoming.length, tone: 'text-secondary-600 bg-secondary-50' },
    { icon: 'ri-group-line', label: 'Spectateurs uniques', value: totalUnique, tone: 'text-accent-600 bg-accent-50' },
    { icon: 'ri-fire-line', label: 'Pic de spectateurs', value: peakOverall, tone: 'text-foreground-600 bg-background-100' },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${c.tone}`}>
              <i className={c.icon}></i>
            </span>
            <p className="text-2xl font-bold font-heading text-foreground-950">{c.value}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>
      {totalWatch > 0 && (
        <p className="text-xs text-foreground-500 flex items-center gap-1.5 -mt-3">
          <i className="ri-timer-line"></i>Temps de visionnage cumulé : <strong>{formatDuration(totalWatch)}</strong>
        </p>
      )}

      {/* Créer un live */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <i className="ri-live-line text-xl text-red-500"></i>
          </span>
          <div>
            <h3 className="text-base font-semibold text-foreground-900">Lancer une diffusion</h3>
            <p className="text-xs text-foreground-500">Un seul animateur diffuse, les autres regardent.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre du live</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Masterclass, Webinaire, Présentation produit…"
              className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Date (vide = maintenant)</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Heure</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-xl bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Durée</label>
              <div className="flex items-center gap-1.5 flex-wrap">
                {DURATION_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setDuration(o.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                      duration === o.value ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => createLive(true)}
              disabled={busy || !title.trim()}
              className="px-5 py-2.5 bg-red-500 text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-red-600 disabled:opacity-40 whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-live-line"></i>Démarrer maintenant
            </button>
            <button
              onClick={() => createLive(false)}
              disabled={busy || !title.trim() || !date}
              className="px-5 py-2.5 bg-secondary-50 text-secondary-700 rounded-full text-sm font-semibold cursor-pointer hover:bg-secondary-100 disabled:opacity-40 whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-calendar-check-line"></i>Programmer
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <>
          {/* À venir */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
              <i className="ri-calendar-event-line mr-1.5"></i>Lives à venir ({upcoming.length})
            </h3>
            {upcoming.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                <p className="text-sm text-foreground-500">Aucun live programmé.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {upcoming.map((m) => (
                  <div key={m.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-event-line text-amber-600"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900 truncate">{m.title}</p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          <i className="ri-time-line mr-1"></i>{formatDateTime(m.scheduled_at)} · {m.duration_minutes} min
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={getMeetingUrl('', m)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-red-500 text-white rounded-full text-xs font-semibold cursor-pointer hover:bg-red-600 whitespace-nowrap flex items-center gap-1.5"
                      >
                        <i className="ri-live-line text-sm"></i>Lancer le live
                      </a>
                      <button
                        onClick={() => copyLink(m)}
                        className="px-3 py-2 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium cursor-pointer hover:bg-secondary-100 whitespace-nowrap"
                      >
                        {copiedId === m.id ? <><i className="ri-check-line mr-1"></i>Copié</> : <><i className="ri-link mr-1"></i>Lien</>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Passés */}
          <div>
            <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
              <i className="ri-history-line mr-1.5"></i>Lives passés ({past.length})
            </h3>
            {past.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                <p className="text-sm text-foreground-500">Aucun live terminé pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {past.map((m) => {
                  const st = statsByMeeting.get(m.id) || { unique: 0, watch: 0 };
                  const peak = Number((m as unknown as { live_peak_viewers?: number }).live_peak_viewers) || 0;
                  return (
                    <div key={m.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${m.status === 'active' ? 'bg-red-50' : 'bg-background-100'}`}>
                            <i className={`${m.status === 'active' ? 'ri-live-line text-red-500' : 'ri-checkbox-circle-line text-foreground-400'}`}></i>
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-900 truncate">{m.title}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-foreground-500">
                                <i className="ri-calendar-line mr-1"></i>{formatDateTime(m.scheduled_at || m.created_at)}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_STYLES[m.status] || ''}`}>
                                {STATUS_LABELS[m.status] || m.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {m.status === 'active' && (
                            <button
                              onClick={() => endLive(m.id)}
                              className="px-3 py-2 bg-red-50 text-red-600 rounded-full text-xs font-semibold cursor-pointer hover:bg-red-100 whitespace-nowrap"
                            >
                              <i className="ri-stop-circle-line mr-1"></i>Terminer
                            </button>
                          )}
                          <a
                            href={getMeetingUrl('', m)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 bg-background-100 text-foreground-600 rounded-full text-xs font-medium cursor-pointer hover:bg-background-200/70 whitespace-nowrap"
                          >
                            <i className="ri-external-link-line mr-1"></i>Salle
                          </a>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-background-200/70">
                        <div className="text-center">
                          <p className="text-base font-bold font-heading text-foreground-900">{st.unique}</p>
                          <p className="text-[10px] text-foreground-500">Spectateurs uniques</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-bold font-heading text-accent-600">{peak}</p>
                          <p className="text-[10px] text-foreground-500">Pic simultané</p>
                        </div>
                        <div className="text-center">
                          <p className="text-base font-bold font-heading text-foreground-900">{formatDuration(st.watch)}</p>
                          <p className="text-[10px] text-foreground-500">Visionnage cumulé</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}